import React, { useContext, useCallback } from 'react';
import _ from 'lodash';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { withRouter } from 'react-router-dom';
import { useModal } from '@jutro/components';
import BoatInformationComponent from '../BoatInformation/BoatInformationComponent';
import metadata from './BoatTabSetComponent.metadata.json5';
import styles from './BoatTabSetComponent.module.scss';
import AdditionalInformationComponent from '../AdditionalInformationComponent/AdditionalInformationComponent';
import NavigationComponent from '../Navigation/NavigationComponent';
import MooringLayupComponent from '../MooringLayup/MooringLayupComponent';
import HighPerformanceVesselSupplementalComponent from '../HighPerformanceVesselSupplemental/HighPerformanceVesselSupplementalComponent';
import AdditionalPartsComponent from '../AdditionalParts/AdditionalPartsComponent';
import ExtNavComponent from '../ExtNavComponent/ExtNavComponent';
import messages from './BoatTabSetComponent.messages';
import { focusNextElement, screenReaderAnnounce } from 'premier-capability-hooks';
import { TranslatorContext } from '@jutro/legacy/locale';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function BoatTabSetComponent(props) {

    const { submissionVM, updateWizardData, pathUpdated, offeringCode, boatVm, viewModelService, index } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const { onValidate } = useValidation('BoatTabSetComponent');
    const modelVM = _.get(submissionVM, pathUpdated)
    const { showConfirm } = useModal();
    const translator = useContext(TranslatorContext);

    modelVM.value.isValidationReq = true

    const deleteBoat = useCallback(
            () => {
                const boatListPath = 'lobData.recMarLine.coverables.boats.value';
                const boat = modelVM.value;
                const boatList = _.get(submissionVM, boatListPath);
    
                showConfirm({
                    title: messages.removeBoat,
                    message: messages.removeBoatDesc,
                    status: 'warning',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.yes,
                    cancelButtonText: messages.no
                }).then(
                    (results) => {
                        if (results === 'cancel') {
                            return _.noop();
                        }
    
                        const newoperatorList = _.remove(boatList,
                            (boatFromList) => _.isEqual(boatFromList, boat));
    
                        _.set(submissionVM, newoperatorList, boatListPath);
                        updateWizardData(submissionVM);

                        screenReaderAnnounce(translator(messages.removeBoatDetails))
                        focusNextElement(true, 0, `addBoatDetails`);
    
                        return true;
                    }, _.noop
                );
            }, [modelVM.value, submissionVM, showConfirm, updateWizardData]
        );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        '@field': {
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        boatInformationComponent: {
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.boatInformation`,
            offeringCode,
            index,
            boatVm,
            viewModelService
        },
        boatAddlInformationComponent: {
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.additionalInformation`,
            offeringCode,
            boatVm,
            viewModelService
        },
        navigationComponent: {
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.navigation`,
            offeringCode,
            boatVm,
            viewModelService
        },
        mooringLayupComponent: {
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.mooringLayup`,
            offeringCode,
            boatVm,
            viewModelService
        },
        highPerformanceVesselSupplementalComponent: {
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.highPerformanceVessel`,
            offeringCode,
            boatVm,
            viewModelService
        },
        additionalPartsComponent: {
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.additionalParts`,
            boatInformationPath: `${pathUpdated}.boatInformation`,
            offeringCode,
            boatVm,
            viewModelService
        },
        boatHighPerformanceVesselSupplementalAccordionCard: {
            visible: !_.isUndefined(boatVm.value.boatInformation.maxSpemphFie) && boatVm.value.boatInformation.maxSpemphFie >= 60,
        },
        extNavAccComponent: { 
            submissionVM,
            updateWizardData,
            pathUpdated: `${pathUpdated}.extendedNavigation`,
            offeringCode,
            boatVm,
            index,
            viewModelService
        },
        extNavAccordionCard: {
            visible: !_.isUndefined(boatVm.value.navigation.doYouReqExtNavOutOCanFie) && boatVm.value.navigation.doYouReqExtNavOutOCanFie === true
        },
        
        
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate,
            deleteBoat
        },
        resolveComponentMap: {
            boatInformationComponent: BoatInformationComponent,
            boatAddlInformationComponent: AdditionalInformationComponent,
            navigationComponent: NavigationComponent,
            mooringLayupComponent: MooringLayupComponent,
            highPerformanceVesselSupplementalComponent: HighPerformanceVesselSupplementalComponent,
            additionalPartsComponent: AdditionalPartsComponent,
            extNavComponent: ExtNavComponent
        },
        resolveClassNameMap: styles,
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.componentContent, _.get(submissionVM, pathUpdated), id, path, overrideProps),
        [overrideProps, pathUpdated, submissionVM]
    );

    const handleValueChange = useCallback(
        (value, changedPath) => {
            const fullPath = `${pathUpdated}.${changedPath}`;

            _.set(submissionVM, `${fullPath}`, value);

            updateWizardData(submissionVM);
        },
        [pathUpdated, submissionVM, updateWizardData]
    );

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            resolveValue={readValue}
            overrideProps={overrideProps}
            onValidationChange={onValidate}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );


}

BoatTabSetComponent.propTypes = wizardProps;
export default withRouter(BoatTabSetComponent);
